// src/pages/WhatsAppRegister.jsx
import React, { useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import config from "../config";
import "./WhatsappRegister.css";

const WhatsAppRegister = ({ userKey: userKeyProp }) => {
  const navigate = useNavigate();

  /* ---------------- Get userKey ---------------- */
  const userKey = useMemo(() => {
    if (userKeyProp) return String(userKeyProp);
    try {
      const raw = sessionStorage.getItem("userData") || "{}";
      const data = JSON.parse(raw);
      const key =
        data?.userKey ??
        data?.data?.userKey ??
        data?.user?.userKey ??
        data?.profile?.userKey ??
        "";
      return key ? String(key) : "";
    } catch {
      return "";
    }
  }, [userKeyProp]);

  /* ---------------- States ---------------- */
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [serverOtp, setServerOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [linkClicked, setLinkClicked] = useState(false); // 👈 New state

  /* ---------------- Helpers ---------------- */
  const sanitizePhone = (v) => v.replace(/\D/g, "").slice(0, 10);

  const phoneForBackend = () => {
    const clean = sanitizePhone(phone);
    return clean.length === 10 ? `91${clean}` : "";
  };

  const pickMsg = (e, fallback) =>
    (e?.response?.data?.message && String(e.response.data.message)) ||
    (typeof e?.response?.data === "string" && e.response.data) ||
    (e?.message && String(e.message)) ||
    fallback;

  const extractOtp = (resData) => {
    if (!resData) return "";
    return (
      String(
        resData.otp ??
          resData.code ??
          resData?.data?.otp ??
          resData?.payload?.otp ??
          ""
      )
        .replace(/\D/g, "")
        .slice(0, 6) || ""
    );
  };

  /* ---------------- 1️⃣ Send OTP ---------------- */
  const handleGetOtp = async () => {
    setError("");
    setMsg("");
    setOtp("");
    setServerOtp("");
    setOtpVerified(false);
    setOtpSent(false);

    const num = phoneForBackend();
    if (!num) {
      setError("Enter a valid 10-digit WhatsApp number.");
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("whatsappNumber", num);

      const res = await axios.post(
        `${config.BASE_URL || config.baseURL}/api/whatsapp/send-otp`,
        params,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );

      const otpFromServer = extractOtp(res?.data);
      if (!otpFromServer) {
        setError("Could not read OTP from server. Please request OTP again.");
        setOtpSent(false);
        return;
      }

      setServerOtp(otpFromServer);
      setOtpSent(true);
      setMsg("OTP sent successfully. Please enter it below.");
    } catch (e) {
      setError(pickMsg(e, "Error while sending OTP."));
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- 2️⃣ Verify OTP ---------------- */
  const handleVerifyOtp = () => {
    setError("");
    setMsg("");

    if (!otp) {
      setError("Enter the OTP to verify.");
      return;
    }
    if (!serverOtp) {
      setError("Verification not available. Please request OTP again.");
      return;
    }

    const entered = String(otp).replace(/\D/g, "").slice(0, 6);
    if (entered && entered === serverOtp) {
      setOtpVerified(true);
      setMsg("OTP verified successfully.");
    } else {
      setOtpVerified(false);
      setError("Invalid OTP.");
    }
  };

  /* ---------------- 3️⃣ Register WhatsApp ---------------- */
  const handleRegister = async () => {
    setError("");
    setMsg("");

    if (!userKey) {
      setError("User details missing. Please login again.");
      setTimeout(() => navigate("/", { replace: true }), 1000);
      return;
    }
    const num = phoneForBackend();
    if (!num) {
      setError("Enter a valid 10-digit WhatsApp number.");
      return;
    }
    if (!otpVerified) {
      setError("Verify OTP first.");
      return;
    }

    const payload = {
      userKey,
      whatsappNumber: num,
      whatsappEnabled,
      otp: String(otp).replace(/\D/g, "").slice(0, 6),
    };

    try {
      setLoading(true);
      const res = await axios.post(
        `${config.BASE_URL || config.baseURL}/api/whatsapp/update-whatsapp`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.status >= 200 && res.status < 300) {
        setMsg("WhatsApp preference saved.");

        try {
          sessionStorage.setItem("profilePhone", String(num));
          localStorage.setItem("profilePhone", String(num));

          const raw = sessionStorage.getItem("userData") || "{}";
          const parsed = JSON.parse(raw || "{}");
          const merged = {
            ...parsed,
            whatsappNumber: String(num),
            phoneNumber: parsed?.phoneNumber || String(num),
            mobile: parsed?.mobile || String(num),
          };
          sessionStorage.setItem("userData", JSON.stringify(merged));
        } catch (mergeErr) {
          console.warn("Could not merge phone into userData:", mergeErr);
        }

        setMsg("WhatsApp preference saved. Redirecting to login…");
        setTimeout(() => navigate("/", { replace: true }), 1000);
      } else {
        setError("Failed to save preference.");
      }
    } catch (e) {
      setError(pickMsg(e, "Error saving preference."));
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- 4️⃣ Skip Button ---------------- */
  const handleSkip = () => {
    navigate("/", { replace: true });
  };

  /* ---------------- UI ---------------- */
  return (
    <div id="roott1">
      <div className="whatsapp-register-container">
        <h2>WhatsApp Registration</h2>
        <p>Link your WhatsApp number to receive important alerts.</p>

        {/* Phone Input */}
        <div className="phone-row">
          <span className="country-code">91</span>
          <input
            type="text"
            value={phone}
            maxLength={10}
            placeholder="10-digit number"
            onChange={(e) => setPhone(sanitizePhone(e.target.value))}
            className="phone-input"
          />
        </div>

        {/* 🔗 WhatsApp link must be clicked first */}
        {!linkClicked ? (
          <div className="whatsapp-link-box">
            <a
              href="https://wa.me/919840139203?text=JOIN%20EMMPPE"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-link"
              onClick={() => setLinkClicked(true)}
            >
              📲 Click here to join EMMPPE on WhatsApp
            </a>
          </div>
        ) : (
          !otpSent && (
            <button
              className="btn primary"
              onClick={handleGetOtp}
              disabled={loading}
            >
              {loading ? "Sending…" : "Get OTP"}
            </button>
          )
        )}

        {/* OTP Section */}
        {otpSent && !otpVerified && (
          <div className="otp-section">
            <input
              type="text"
              value={otp}
              maxLength={6}
              placeholder="Enter OTP"
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              className="otp-input"
            />
            <button
              className="btn verify"
              onClick={handleVerifyOtp}
              disabled={loading || !otp}
            >
              Verify OTP
            </button>
            {!serverOtp && (
              <div className="error-hint">
                Can’t verify: server didn’t return an OTP. Please tap “Get OTP”
                again.
              </div>
            )}
          </div>
        )}

        {/* Toggle + Register */}
        {otpVerified && (
          <div className="register-section">
            <div className="toggle-row">
              <span className="toggle-label">Receive Data:</span>

              {/* 🔘 SAME TOGGLE AS SETTING PAGE */}
              <label className="small-toggle">
                <input
                  type="checkbox"
                  checked={whatsappEnabled}
                  onChange={() =>
                    setWhatsappEnabled((prev) => !prev)
                  }
                />
                <span
                  className={`small-toggle-pill ${
                    whatsappEnabled ? "on" : "off"
                  }`}
                >
                  <span className="small-toggle-dot" />
                  <span className="small-toggle-label">
                    {whatsappEnabled ? "ON" : "OFF"}
                  </span>
                </span>
              </label>
            </div>

            <button
              className="btn primary"
              onClick={handleRegister}
              disabled={loading}
            >
              {loading ? "Saving…" : "Register"}
            </button>
          </div>
        )}

        {/* Message / Error */}
        {(msg || error) && (
          <div className={`msg-box ${error ? "error" : "success"}`}>
            {error || msg}
          </div>
        )}

        {/* ✅ Skip Button */}
        {!phone && !otpSent && (
          <div className="skip-row">
            <button
              className="btn secondary"
              onClick={handleSkip}
              disabled={loading}
            >
              Skip
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppRegister;